import process from 'node:process';
import { getArgValue, hasFlag } from './auth.js';
import { writeRawOutput } from './brand.js';
import { apiRequest } from './cli-helpers.js';
import { truncate } from './workflow-format.js';

export type WorkflowListPagination = {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
};

export type WorkflowListItem = {
  id: string;
  name: string;
  status: string;
  flowType: string;
  nodeCount?: number;
};

export type WorkflowListResponse = {
  ok: boolean;
  error?: string;
  data: WorkflowListItem[];
  pagination: WorkflowListPagination;
};

export function formatWorkflowListTable(args: {
  data: readonly WorkflowListItem[];
  pagination: WorkflowListPagination;
}): string {
  const lines = [
    `Workflows: ${args.pagination.totalItems} total (page ${args.pagination.page}/${args.pagination.totalPages})`,
    '',
  ];

  if (args.data.length === 0) {
    lines.push('No workflows found.');
    return lines.join('\n');
  }

  const idW = 36;
  const nameW = 30;
  const statusW = 12;
  const typeW = 10;

  lines.push(
    ['ID'.padEnd(idW), 'Name'.padEnd(nameW), 'Status'.padEnd(statusW), 'Type'.padEnd(typeW)].join(
      '  '
    )
  );

  for (const workflow of args.data) {
    lines.push(
      [
        workflow.id.padEnd(idW),
        truncate(workflow.name, nameW).padEnd(nameW),
        workflow.status.padEnd(statusW),
        workflow.flowType.padEnd(typeW),
      ].join('  ')
    );
  }

  return lines.join('\n');
}

export async function handleWorkflowList(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const jsonOutput = hasFlag(argv, '--json');

  const params = new URLSearchParams();
  const search = getArgValue(argv, '--search');
  const flowType = getArgValue(argv, '--flow-type');
  const status = getArgValue(argv, '--status');
  const page = getArgValue(argv, '--page');
  const pageSize = getArgValue(argv, '--page-size');

  if (search) params.set('search', search);
  if (flowType) params.set('flowType', flowType);
  if (status) params.set('status', status);
  if (page) params.set('page', page);
  if (pageSize) params.set('pageSize', pageSize);

  const qs = params.toString();
  const path = `/workflows${qs ? `?${qs}` : ''}`;

  const result = await apiRequest<WorkflowListResponse>(apiUrl, token, 'GET', path);

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify({ data: result.data, pagination: result.pagination }, null, 2));
    return;
  }

  writeRawOutput(formatWorkflowListTable({ data: result.data, pagination: result.pagination }));
}
